import React, { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import "./InlineFeedback.css";

// Shared event pub-sub for feedback messages
const feedbackListeners = new Set();

export const feedbackBus = {
  subscribe(callback) {
    feedbackListeners.add(callback);
    return () => feedbackListeners.delete(callback);
  },
  emit(event) {
    feedbackListeners.forEach((callback) => {
      try {
        callback(event);
      } catch (err) {
        console.error("Feedback listener error:", err);
      }
    });
  },
};

// SVG icons matching Figma Design System
function AlertCircleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function AlertTriangleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

const ICONS = {
  error: AlertCircleIcon,
  success: CheckCircleIcon,
  warning: AlertTriangleIcon,
  info: InfoIcon,
};

/**
 * InlineFormMessage / InlineFeedback
 * Displays contextual banners for forms, pages, or actions matching Figma tokens.
 */
export function InlineFormMessage({
  message,
  type = "info",
  status,
  scope = "global",
  onClose,
  autoClose = 5000,
  className = "",
  style = {},
}) {
  const [currentMessage, setCurrentMessage] = useState(message || "");
  const [currentType, setCurrentType] = useState(() => {
    if (typeof status === "boolean") return status ? "success" : "error";
    return type || "info";
  });
  const timerRef = useRef(null);

  // Sync controlled props
  useEffect(() => {
    if (message !== undefined) {
      setCurrentMessage(message || "");
      if (typeof status === "boolean") {
        setCurrentType(status ? "success" : "error");
      } else if (type) {
        setCurrentType(type);
      }
    }
  }, [message, type, status]);

  // Subscribe to feedback bus when no explicit controlled message is set
  useEffect(() => {
    const unsubscribe = feedbackBus.subscribe((evt) => {
      // Check scope matching: exact match or global listener catching unscoped/global
      if (
        evt.scope === scope ||
        (scope === "global" && (!evt.scope || evt.scope === "global"))
      ) {
        setCurrentMessage(evt.message);
        setCurrentType(evt.type || (evt.status ? "success" : "error"));

        if (timerRef.current) clearTimeout(timerRef.current);
        const duration = evt.duration !== undefined ? evt.duration : autoClose;
        if (duration > 0) {
          timerRef.current = setTimeout(() => {
            setCurrentMessage("");
          }, duration);
        }
      }
    });

    return () => {
      unsubscribe();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [scope, autoClose]);

  // Handle auto-close for controlled messages
  useEffect(() => {
    if (currentMessage && autoClose > 0 && message !== undefined) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        handleDismiss();
      }, autoClose);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentMessage, autoClose, message]);

  const handleDismiss = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setCurrentMessage("");
    if (typeof onClose === "function") {
      onClose();
    }
  }, [onClose]);

  if (!currentMessage) return null;

  const resolvedType = ["error", "success", "warning", "info"].includes(
    currentType
  )
    ? currentType
    : "info";
  const IconComponent = ICONS[resolvedType] || InfoIcon;

  return (
    <div
      className={`inline-feedback-banner inline-feedback-banner--${resolvedType} ${className}`}
      style={style}
      role={resolvedType === "error" ? "alert" : "status"}
      aria-live="polite"
    >
      <span className="inline-feedback-icon">
        <IconComponent />
      </span>
      <div className="inline-feedback-text">{currentMessage}</div>
      <button
        type="button"
        className="inline-feedback-close"
        onClick={handleDismiss}
        aria-label="Dismiss message"
      >
        <CloseIcon />
      </button>
    </div>
  );
}

InlineFormMessage.propTypes = {
  message: PropTypes.node,
  type: PropTypes.oneOf(["error", "success", "warning", "info"]),
  status: PropTypes.bool,
  scope: PropTypes.string,
  onClose: PropTypes.func,
  autoClose: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
};

/**
 * InlineFieldMessage
 * 12px Lexend validation error or status directly below an input field.
 */
export function InlineFieldMessage({
  error,
  message,
  type = "error",
  children,
  className = "",
  style = {},
}) {
  const content = error || message || children;
  if (!content) return null;

  const isError = type === "error" || Boolean(error);
  const Icon = isError ? AlertCircleIcon : CheckCircleIcon;

  return (
    <span
      className={`${
        isError ? "inline-field-error" : "inline-field-success"
      } ${className}`}
      style={style}
      role={isError ? "alert" : "status"}
    >
      <Icon />
      <span>{content}</span>
    </span>
  );
}

InlineFieldMessage.propTypes = {
  error: PropTypes.node,
  message: PropTypes.node,
  type: PropTypes.oneOf(["error", "success"]),
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object,
};

/**
 * InlineActionMessage
 * Compact pill/badge displayed immediately next to or below action buttons.
 */
export function InlineActionMessage({
  message,
  type = "info",
  status,
  onClose,
  className = "",
  style = {},
}) {
  if (!message) return null;

  const resolvedType =
    typeof status === "boolean"
      ? status
        ? "success"
        : "error"
      : type || "info";
  const IconComponent = ICONS[resolvedType] || InfoIcon;

  return (
    <span
      className={`inline-action-message inline-action-message--${resolvedType} ${className}`}
      style={style}
      role="status"
    >
      <IconComponent />
      <span>{message}</span>
      {typeof onClose === "function" && (
        <button
          type="button"
          className="inline-feedback-close"
          style={{ width: "16px", height: "16px", flex: "0 0 16px" }}
          onClick={onClose}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      )}
    </span>
  );
}

InlineActionMessage.propTypes = {
  message: PropTypes.node,
  type: PropTypes.oneOf(["error", "success", "warning", "info"]),
  status: PropTypes.bool,
  onClose: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object,
};

/**
 * Hook to manage inline feedback in any component.
 */
export function useInlineFeedback(defaultScope = "local") {
  const [feedback, setFeedback] = useState({ message: "", type: "info" });

  const showFeedback = useCallback(
    (message, type = "info", duration = 4000) => {
      setFeedback({ message, type });
      if (duration > 0) {
        setTimeout(() => {
          setFeedback((prev) =>
            prev.message === message ? { message: "", type: "info" } : prev
          );
        }, duration);
      }
    },
    []
  );

  const clearFeedback = useCallback(() => {
    setFeedback({ message: "", type: "info" });
  }, []);

  return {
    feedback,
    showFeedback,
    clearFeedback,
    showError: (msg, dur) => showFeedback(msg, "error", dur || 5000),
    showSuccess: (msg, dur) => showFeedback(msg, "success", dur || 3500),
    showWarning: (msg, dur) => showFeedback(msg, "warning", dur || 4000),
    showInfo: (msg, dur) => showFeedback(msg, "info", dur || 4000),
  };
}

export const InlineFeedback = InlineFormMessage;
export default InlineFormMessage;
